<script setup lang="ts">
  import type { PrimitiveProps } from 'reka-ui';
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import type { HTMLAttributes } from 'vue';
  import WaveSurfer from 'wavesurfer.js';

  import { Button } from '@ui/components/button';
  import { Skeleton } from '@ui/components/skeleton';
  import { cn } from '@ui/utils/utils';

  const props = withDefaults(defineProps<Props>(), {
    size: 50,
  });

  const emit = defineEmits<{
    (e: 'timeupdate', time: number): void;
  }>();

  interface Props extends PrimitiveProps {
    class?: HTMLAttributes['class'];
    size?: number;
    src: string;
  }

  const currentTime = ref(0);
  const duration = ref(0);
  const isPlaying = ref(false);
  const isReady = ref(false);
  const isLoading = ref(false);
  const loadingProgress = ref(0);
  const isError = ref(false);
  const wavesurferRef = ref();
  const wavesurfer = ref<WaveSurfer>();

  onMounted(() => {
    try {
      wavesurfer.value = WaveSurfer.create({
        backend: 'MediaElement',
        barGap: 1.5,
        barHeight: 1.5,
        barRadius: 5,
        barWidth: 2,
        container: wavesurferRef.value,
        cursorColor: '#FFDDDD',
        dragToSeek: true,
        height: props.size,
        progressColor: '#2784FF',
        url: props.src,
      });

      wavesurfer.value!.on('ready', (audioDuration) => {
        isReady.value = true;

        isError.value = false;
        isLoading.value = false;
        loadingProgress.value = 0;

        duration.value = audioDuration;
      });

      wavesurfer.value!.on('loading', (percent) => {
        isLoading.value = true;

        isError.value = false;
        loadingProgress.value = percent;
        isReady.value = false;

        if (percent === 100) {
          loadingProgress.value = percent;
        }
      });

      wavesurfer.value!.on('error', (e) => {
        isError.value = true;

        isLoading.value = false;
        loadingProgress.value = 0;
        isReady.value = false;
      });

      wavesurfer.value!.on('play', () => {
        isPlaying.value = true;
      });

      /** When the audio pauses */
      wavesurfer.value!.on('pause', () => {
        isPlaying.value = false;
      });

      /** When the audio finishes playing */
      wavesurfer.value!.on('finish', () => {
        isPlaying.value = false;
      });

      wavesurfer.value!.on('timeupdate', (currTime) => {
        emit('timeupdate', currTime);
        currentTime.value = currTime;
      });
    } catch (e) {
      console.error(e);
    }
  });

  onBeforeUnmount(() => {
    wavesurfer.value!.unAll();
  });

  const formatSeconds = (s: number) => {
    s = Math.floor(s);
    const minutes = Math.floor(s / 60).toString();
    const remainder = (s % 60).toString();
    return `${padString(minutes)}:${padString(remainder)}`;
  };

  const padString = (s: string) => (s.length === 1 ? `0${s}` : s);

  const seekTo = (time: number) => {
    wavesurfer.value?.seekTo(Math.min(time / duration.value, 1));
  };

  defineExpose({
    currentTime: computed(() => currentTime.value),
    isPlaying,
    seekTo,
    wavesurfer,
  });
</script>

<template>
  <div
    :class="
      cn('grid grid-cols-[auto_1fr_auto] bg-secondary rounded gap-x-2 px-3 relative', props.class)
    "
  >
    <Skeleton v-show="isLoading" class="h-full w-full absolute top-0 left-0" />

    <div class="w-full flex justify-center">
      <div v-if="isError" class="my-auto h-fit w-[25px] text-center text-base">
        <i class="fa text-destructive fa-exclamation-circle" />
      </div>

      <Button
        v-else
        variant="ghost"
        size="icon"
        class="hover:bg-transparent my-auto h-fit w-[20px] text-center text-base"
        @click="isPlaying ? wavesurfer?.pause() : wavesurfer?.play()"
      >
        <i
          class="fa fas text-2xs!"
          :class="{
            'fa-pause': isPlaying,
            'fa-play': !isPlaying,
          }"
        />
      </Button>
    </div>

    <div class="flex w-full h-fit relative">
      <div v-if="!isReady" class="absolute place-self-center w-full h-fit text-xs overflow-hidden">
        <span v-if="isLoading" class="space-x-2">
          <i class="fa fa-spinner animate-spin" />

          <span v-if="loadingProgress === 100">Getting things ready...</span>
          <span v-else>Loading...</span>
        </span>

        <p v-else-if="isError" class="text-error truncate">
          <span class="font-bold">Error:</span> Audio failed to load. Try reloading or downloading
          the file.
        </p>
      </div>

      <div ref="wavesurferRef" class="w-full h-fit" />
    </div>

    <div class="flex flex-col justify-center text-sm font-medium w-[45px] text-right">
      <span>
        {{ currentTime > 0 ? formatSeconds(currentTime) : formatSeconds(duration) }}
      </span>
    </div>
  </div>
</template>
